/**
 * Recognising guest-list column headers in the languages our customers actually use.
 *
 * The original matcher only knew English words and matched them as substrings, so an
 * Italian sheet headed "Cognome / Nome" matched nothing and every column had to be mapped
 * by hand. Teaching the substring matcher the word "nome" would be worse than useless,
 * because "cognome" contains it and every surname column would become a first name.
 *
 * So localized headers are matched on the whole header, normalized, never as a substring.
 * "cognome" is the surname, "nome" is the first name, and the two can never collide. The
 * English substring matcher still runs afterwards for anything left over, which keeps
 * headers like "Guest first name" working as before.
 *
 * Surnames are checked before first names on purpose: several languages build the surname
 * out of the word for name (nachname, efternamn, sobrenome), and the more specific term
 * has to win.
 */

/**
 * Letters that survive accent stripping because they are their own character rather than
 * a letter with a mark: Turkish dotless i, Polish crossed l, Nordic slashed o and so on.
 * Without these, "Soyadı" and "Spółka" never match their entries below.
 */
const standaloneLetters = [
  [/ı/g, 'i'], [/i̇/g, 'i'], [/ł/g, 'l'], [/ø/g, 'o'], [/æ/g, 'ae'], [/œ/g, 'oe'],
  [/đ/g, 'd'], [/ð/g, 'd'], [/þ/g, 'th'], [/ß/g, 'ss'], [/ħ/g, 'h'], [/ŀ/g, 'l'],
];

/** Lowercase, strip accents, turn punctuation into spaces, collapse the rest. */
export function normalizeHeader(value) {
  if (value === null || value === undefined) return '';

  const lowercased = standaloneLetters.reduce(
    (text, [pattern, replacement]) => text.replace(pattern, replacement),
    value.toString().trim().toLowerCase(),
  );

  return lowercased
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // combining accents: é becomes e, ö becomes o, ό becomes ο
    .replace(/[_\-./\\|()[\]{}:;,*#"']/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Whole-header terms per guest property. Written naturally here and normalized on load,
 * so accented and punctuated forms do not need separate entries.
 */
const localizedHeaders = {
  lastName: [
    'last name', 'lastname', 'surname', 'family name', 'second name',
    'cognome', // Italian
    'apellido', 'apellidos', 'primer apellido', // Spanish
    'nom', 'nom de famille', // French
    'nachname', 'familienname', // German
    'sobrenome', 'apelido', // Portuguese
    'achternaam', // Dutch
    'nazwisko', // Polish
    'efternamn', // Swedish
    'efternavn', 'etternavn', // Danish, Norwegian
    'sukunimi', // Finnish
    'eftirnafn', // Icelandic
    'prijmeni', 'priezvisko', // Czech, Slovak
    'nume', 'nume de familie', // Romanian
    'vezeteknev', 'csaladi nev', // Hungarian
    'prezime', 'priimek', 'priimenis', 'prezvime', // Croatian, Slovene, Lithuanian
    'uzvards', 'perekonnanimi', // Latvian, Estonian
    'soyad', 'soyadi', // Turkish
    'eponymo', // Greek
    'familia', 'prizvishche', // Russian, Ukrainian transliterated
    'фамилия', 'прізвище', 'презиме', // Cyrillic
    'επωνυμο', // Greek
    'اللقب', 'اسم العائلة', 'الكنية', // Arabic
    'nama belakang', 'nama keluarga', // Indonesian, Malay
    'ho', 'ho va ten dem', // Vietnamese
    'namsakun', 'นามสกุล', // Thai
    '姓', '姓氏', '苗字', // Chinese, Japanese
    '성', // Korean
  ],
  firstName: [
    'first name', 'firstname', 'given name', 'forename', 'christian name', 'name',
    'nome', // Italian, Portuguese
    'nombre', 'nombres', // Spanish
    'prenom', 'prenoms', // French
    'vorname', // German
    'voornaam', // Dutch
    'imie', // Polish
    'fornamn', // Swedish
    'fornavn', // Danish, Norwegian
    'etunimi', // Finnish
    'fornafn', // Icelandic
    'jmeno', 'krstne meno', // Czech, Slovak
    'prenume', // Romanian
    'keresztnev', 'utonev', // Hungarian
    'ime', 'vardas', // Croatian and Slovene, Lithuanian
    'vards', 'eesnimi', // Latvian, Estonian
    'ad', 'isim', 'adi', // Turkish
    'onoma', // Greek
    'imya', // Russian transliterated
    'имя', "ім'я", 'име', // Cyrillic
    'ονομα', // Greek
    'الاسم', 'الاسم الاول', 'اسم', // Arabic
    'nama depan', 'nama awal', // Indonesian, Malay
    'ten', // Vietnamese
    'chue', 'ชื่อ', // Thai
    '名', '名前', // Chinese, Japanese. 名字 is deliberately absent: it is the given name in Chinese and the surname in Japanese
    '이름', // Korean
  ],
  email: [
    'email', 'e mail', 'email address', 'mail', 'electronic mail',
    'correo', 'correo electronico', 'e correo', // Spanish
    'courriel', 'adresse email', 'adresse e mail', // French
    'posta elettronica', 'indirizzo email', 'e mail lavoro', 'email lavoro', 'email privata', // Italian
    'e mail adresse', 'mailadresse', // German
    'e mailadres', // Dutch
    'adres e mail', 'poczta', // Polish
    'e post', 'epost', 'e postadress', // Swedish, Norwegian, Danish
    'sahkoposti', // Finnish
    'netfang', // Icelandic
    'e mailova adresa', // Czech
    'posta', 'eposta', 'e posta', // Turkish, Romanian
    'email cim', // Hungarian
    'elektronnaya pochta', 'pochta', // Russian transliterated
    'почта', 'эл почта', 'електронна пошта', // Cyrillic
    'ηλεκτρονικο ταχυδρομειο', // Greek
    'البريد', 'البريد الالكتروني', // Arabic
    'surat elektronik', // Indonesian
    'thu dien tu', // Vietnamese
    'อีเมล', // Thai
    '邮箱', '电子邮件', '電子郵件', 'メール', 'メールアドレス', // Chinese, Japanese
    '이메일', // Korean
  ],
  phone: [
    'phone', 'phone number', 'telephone', 'mobile', 'mobile number', 'cell', 'cellphone', 'tel',
    'telefono', 'cellulare', 'numero di telefono', // Italian, Spanish
    'movil', 'celular', 'numero de telefono', // Spanish, Portuguese
    'telephone portable', 'portable', 'numero de telephone', // French
    'telefon', 'handy', 'mobiltelefon', 'telefonnummer', // German, Nordic, Polish, Turkish
    'telefoon', 'mobiel', 'telefoonnummer', // Dutch
    'numer telefonu', 'komorka', // Polish
    'puhelin', 'puhelinnumero', // Finnish
    'simi', // Icelandic
    'telefonni cislo', // Czech
    'telefonszam', // Hungarian
    'numar de telefon', // Romanian
    'telefonas', 'talrunis', // Lithuanian, Latvian
    'telefono arithmos', // Greek transliterated
    'телефон', 'номер телефона', // Cyrillic
    'τηλεφωνο', // Greek
    'الهاتف', 'رقم الهاتف', 'الجوال', // Arabic
    'nomor telepon', 'telepon', // Indonesian
    'so dien thoai', // Vietnamese
    'โทรศัพท์', // Thai
    '电话', '手机', '電話', '電話番号', '携帯', // Chinese, Japanese
    '전화', '전화번호', // Korean
  ],
  company: [
    'company', 'organization', 'organisation', 'employer', 'business', 'firm', 'agency', 'brand',
    'azienda', 'societa', 'ditta', 'impresa', // Italian
    'empresa', 'compania', 'organizacion', // Spanish, Portuguese
    'entreprise', 'societe', 'organisme', // French
    'unternehmen', 'firma', 'gesellschaft', // German
    'bedrijf', 'organisatie', // Dutch
    'spolka', 'przedsiebiorstwo', 'organizacja', // Polish
    'foretag', 'bolag', // Swedish
    'virksomhed', 'selskap', 'bedrift', // Danish, Norwegian
    'yritys', // Finnish
    'fyrirtaeki', // Icelandic
    'spolecnost', // Czech
    'ceg', 'vallalat', // Hungarian
    'companie', 'firma comerciala', // Romanian
    'imone', 'uznemums', 'ettevote', // Lithuanian, Latvian, Estonian
    'sirket', 'kurum', // Turkish
    'etaireia', // Greek transliterated
    'компания', 'организация', 'фирма', // Cyrillic
    'εταιρεια', // Greek
    'الشركة', 'المؤسسة', // Arabic
    'perusahaan', 'syarikat', // Indonesian, Malay
    'cong ty', // Vietnamese
    'บริษัท', // Thai
    '公司', '企业', '会社', '企業', // Chinese, Japanese
    '회사', // Korean
  ],
  notes: [
    'note', 'notes', 'comment', 'comments', 'remark', 'remarks', 'description',
    'nota', 'note aggiuntive', 'osservazioni', // Italian
    'notas', 'observaciones', 'comentarios', // Spanish, Portuguese
    'remarque', 'remarques', 'commentaire', 'commentaires', // French
    'notiz', 'notizen', 'anmerkung', 'anmerkungen', 'bemerkungen', // German
    'notitie', 'opmerkingen', // Dutch
    'notatki', 'uwagi', 'komentarz', // Polish
    'anteckningar', 'kommentar', 'noter', 'merknader', // Nordic
    'muistiinpanot', 'huomautukset', // Finnish
    'poznamky', // Czech
    'megjegyzes', // Hungarian
    'observatii', // Romanian
    'pastabos', 'piezimes', 'markused', // Lithuanian, Latvian, Estonian
    'notlar', 'aciklama', // Turkish
    'simeioseis', // Greek transliterated
    'примечание', 'примечания', 'комментарий', // Cyrillic
    'σημειωσεις', // Greek
    'ملاحظات', // Arabic
    'catatan', // Indonesian
    'ghi chu', // Vietnamese
    'หมายเหตุ', // Thai
    '备注', '備考', 'メモ', '備註', // Chinese, Japanese
    '메모', '비고', // Korean
  ],
  vip: ['vip', 'v i p'],
  status: [
    'status', 'guest status', 'rsvp', 'attendance',
    'stato', 'estado', 'statut', 'etat', 'zustand', 'toestand',
    'statut invite', 'sytuacja', 'tila', 'stav', 'allapot', 'durum',
    'katastasi', 'статус', 'состояние', 'κατασταση', 'الحالة',
    'status tamu', 'trang thai', 'สถานะ', '状态', '狀態', 'ステータス', '상태',
  ],
  totalExtraGuests: [
    'extra guests', 'total extras', 'total extra s', 'plus one', 'plus ones', 'guests brought', 'accompanying',
    'accompagnatori', 'ospiti extra', // Italian
    'acompanantes', 'invitados extra', // Spanish
    'accompagnants', 'invites supplementaires', // French
    'begleitung', 'begleitpersonen', 'zusatzliche gaste', // German
    'introduces', 'extra gasten', // Dutch
    'osoby towarzyszace', 'dodatkowi goscie', // Polish
    'medfoljande', 'ekstra gjester', 'ekstra gaester', // Nordic
    'seuralaiset', // Finnish
    'doprovod', // Czech
    'kiserok', // Hungarian
    'insotitori', // Romanian
    'refakatci', // Turkish
    'сопровождающие', 'المرافقون',
    '同行者', '同伴', '동반자',
  ],
};

/** Surname before first name, then the unambiguous fields. */
const matchOrder = ['lastName', 'firstName', 'email', 'phone', 'company', 'totalExtraGuests', 'status', 'vip', 'notes'];

const normalizedHeaders = Object.entries(localizedHeaders).reduce((all, [property, terms]) => {
  const set = new Set();
  terms.forEach((term) => {
    const normalized = normalizeHeader(term);
    if (normalized) set.add(normalized);
  });

  return { ...all, [property]: set };
}, {});

/**
 * The guest property a header cell names, or '' when nothing matches it exactly.
 * @param {*} cellValue raw cell from the sheet
 * @param {string[]} takenProperties properties already claimed by earlier columns
 */
export function matchLocalizedColumn(cellValue, takenProperties = []) {
  const header = normalizeHeader(cellValue);

  if (!header || header.length > 40) return '';

  for (let index = 0; index < matchOrder.length; index += 1) {
    const property = matchOrder[index];

    if (!takenProperties.includes(property) && normalizedHeaders[property].has(header)) {
      return property;
    }
  }

  return '';
}

export default { normalizeHeader, matchLocalizedColumn };
